// src/pages/MeasureApp.jsx
import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

export default function MeasureApp({ onClose }) {
  const containerRef = useRef(null);   // gde kačimo WebGL canvas
  const overlayRef = useRef(null);     // DOM overlay root (za UI dugmiće i labele)
  const [active, setActive] = useState(false);
  const [mode, setMode] = useState("distance"); // "distance" | "height"

  // three/xr refs
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const refSpaceRef = useRef(null);
  const hitSourceRef = useRef(null);
  const reticleRef = useRef(null);

  // measure refs
  const pointsRef = useRef([]);
  const linesRef  = useRef([]);
  const labelsRef = useRef([]); // {el: HTMLDivElement, pos: THREE.Vector3}

  // ===== Helpers =====
  const createLabel = (text, position) => {
    const el = document.createElement("div");
    el.textContent = text;
    el.style.position = "absolute";
    el.style.color = "white";
    el.style.background = "rgba(0,0,0,0.7)";
    el.style.padding = "4px 8px";
    el.style.borderRadius = "6px";
    el.style.pointerEvents = "none";
    overlayRef.current.appendChild(el);
    labelsRef.current.push({ el, pos: position.clone() });
  };

  const updateLabels = () => {
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    if (!camera || !renderer) return;

    labelsRef.current.forEach(({ el, pos }) => {
      const v = pos.clone().project(camera);
      const x = (v.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-v.y * 0.5 + 0.5) * window.innerHeight;
      el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
    });
  };

  const clearMeasurements = () => {
    const scene = sceneRef.current;
    if (!scene) return;

    pointsRef.current.forEach((m) => scene.remove(m));
    linesRef.current.forEach((l) => scene.remove(l));
    labelsRef.current.forEach(({ el }) => el.remove());

    pointsRef.current = [];
    linesRef.current = [];
    labelsRef.current = [];
  };

  const endSession = async () => {
    try {
      const session = rendererRef.current?.xr?.getSession();
      if (session) await session.end();
    } catch {}
  };

  // ===== AR init (auto-start) =====
  useEffect(() => {
    let renderer, scene, camera, reticle, controller;

    const boot = async () => {
      if (!("xr" in navigator)) {
        alert("WebXR nije podržan u ovom pregledaču.");
        onClose?.();
        return;
      }
      const ok = await navigator.xr.isSessionSupported("immersive-ar").catch(() => false);
      if (!ok) {
        alert("AR nije podržan na ovom uređaju.");
        onClose?.();
        return;
      }

      // three
      scene = new THREE.Scene();
      sceneRef.current = scene;

      camera = new THREE.PerspectiveCamera();
      cameraRef.current = camera;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.xr.enabled = true;
      renderer.setSize(window.innerWidth, window.innerHeight);
      rendererRef.current = renderer;

      containerRef.current.appendChild(renderer.domElement);

      scene.add(new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1));

      // reticle
      reticle = new THREE.Mesh(
        new THREE.RingGeometry(0.08, 0.1, 32).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial({ color: 0x00ff00, side: THREE.DoubleSide })
      );
      reticle.matrixAutoUpdate = false;
      reticle.visible = false;
      scene.add(reticle);
      reticleRef.current = reticle;

      // controller (tap)
      controller = renderer.xr.getController(0);
      controller.addEventListener("select", onSelect);
      scene.add(controller);

      // start XR session
      const session = await navigator.xr.requestSession("immersive-ar", {
        requiredFeatures: ["hit-test", "local-floor"],
        optionalFeatures: ["dom-overlay"],
        domOverlay: { root: overlayRef.current }
      }).catch((e) => {
        console.error(e);
        return null;
      });

      if (!session) {
        alert("Neuspešno pokretanje AR sesije.");
        onClose?.();
        return;
      }

      setActive(true);
      renderer.xr.setSession(session);

      session.addEventListener("end", () => {
        setActive(false);
        clearMeasurements();
        renderer.setAnimationLoop(null);
        try {
          containerRef.current?.removeChild(renderer.domElement);
        } catch {}
        renderer.dispose();
        onClose?.();
      });

      const refSpace = await session.requestReferenceSpace("local-floor");
      refSpaceRef.current = refSpace;

      const viewerSpace = await session.requestReferenceSpace("viewer");
      hitSourceRef.current = await session.requestHitTestSource({ space: viewerSpace });

      // render petlja
      renderer.setAnimationLoop((time, frame) => {
        if (frame && hitSourceRef.current) {
          const hits = frame.getHitTestResults(hitSourceRef.current);
          if (hits.length) {
            const pose = hits[0].getPose(refSpaceRef.current);
            if (pose) {
              reticle.visible = true;
              reticle.matrix.fromArray(pose.transform.matrix);
            }
          } else {
            reticle.visible = false;
          }
        }

        updateLabels();
        renderer.render(scene, camera);
      });
    };

    const onSelect = () => {
      const ret = reticleRef.current;
      const scene = sceneRef.current;
      if (!ret?.visible || !scene) return;

      // tačka
      const pt = new THREE.Mesh(
        new THREE.SphereGeometry(0.01, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0xff3333 })
      );
      pt.position.setFromMatrixPosition(ret.matrix);
      scene.add(pt);
      pointsRef.current.push(pt);

      // distance mod: posle 2 tačke => linija + label
      if (mode === "distance") {
        if (pointsRef.current.length === 2) {
          const a = pointsRef.current[0].position;
          const b = pointsRef.current[1].position;

          const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
          const mat = new THREE.LineBasicMaterial({ color: 0x3366ff });
          const line = new THREE.Line(geo, mat);
          scene.add(line);
          linesRef.current.push(line);

          const mid = a.clone().add(b).multiplyScalar(0.5);
          const dist = a.distanceTo(b);
          createLabel(`${dist.toFixed(2)} m`, mid);
        }
        if (pointsRef.current.length > 2) {
          // reset za sledeće merenje
          clearMeasurements();
          pointsRef.current.push(pt);
        }
      }

      // height mod: posle 2 tačke koristi samo ΔY
      if (mode === "height") {
        if (pointsRef.current.length === 2) {
          const a = pointsRef.current[0].position;
          const b = pointsRef.current[1].position;

          const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
          const mat = new THREE.LineBasicMaterial({ color: 0x33cc66 });
          const line = new THREE.Line(geo, mat);
          scene.add(line);
          linesRef.current.push(line);

          const mid = a.clone().add(b).multiplyScalar(0.5);
          const height = Math.abs(b.y - a.y);
          createLabel(`${height.toFixed(2)} m`, mid);
        }
        if (pointsRef.current.length > 2) {
          clearMeasurements();
          pointsRef.current.push(pt);
        }
      }
    };

    boot();

    // cleanup ako se komponenta demontira
    return () => {
      endSession();
    };
    
  }, [mode]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90">
      {/* WebGL canvas mount target */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* XR DOM overlay (UI, labele) */}
      <div ref={overlayRef} className="absolute inset-0 pointer-events-none">
        {/* top bar */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 pointer-events-auto">
          <div className="px-3 py-1 rounded bg-black/60 text-white text-sm">
            {active ? "AR Measure Active" : "Starting AR…"}
          </div>
        </div>

        {/* bottom controls */}
        {active && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-auto">
            <button
              onClick={() => setMode((m) => (m === "distance" ? "height" : "distance"))}
              className="px-4 py-2 rounded bg-blue-600 text-white shadow"
            >
              {mode === "distance" ? "Distance" : "Height"}
            </button>
            <button
              onClick={clearMeasurements}
              className="px-4 py-2 rounded bg-red-600 text-white shadow"
            >
              Clear
            </button>
            <button
              onClick={endSession}
              className="px-4 py-2 rounded bg-gray-700 text-white shadow"
            >
              Exit
            </button>
          </div>
        )}

        {/* close (safety) */}
        <button
          onClick={endSession}
          className="absolute top-3 right-3 pointer-events-auto w-10 h-10 rounded-full bg-black/70 text-white text-xl leading-10 text-center"
          aria-label="Close AR"
          title="Close AR"
        >
          ×
        </button>
      </div>
    </div>
  );
}
